import difference from "lodash/difference";
import filter from "lodash/filter";
import find from "lodash/find";
import forEach from "lodash/forEach";
import includes from "lodash/includes";
import map from "lodash/map";
import omit from "lodash/omit";
import values from "lodash/values";
import { Rect } from "yfiles";
import { NODE_GROUP_MIN_SIZE, NODE_MIN_SIZE, UNDO_ENGINE_AUTO_MERGE_TIME } from "../../../constants";
import GroupNodesSource from "../../../types/groupNodesSource";
import morphLayout from "../../morphLayout";
import getNodesMaps from "../../nodeOrEdgeSourceDataHandling/getNodesMaps";
import nodeSourceFactory from "../../nodeOrEdgeSourceDataHandling/nodeSourceFactory";
import createEdge from "../../updates/createEdge";
import createNode from "../../updates/createNode";
import { addHiddenGroup } from "./getGroupedNodesMap";
import pick from "lodash/pick";
import addOrUpdateGroupNodesLabel from "../../nodeRendering/addOrUpdateGroupNodesLabel";
let timer = null;
export const buildOnRowsPinCallback = ({ graphComponentRef, sourceDataRef, documentId, documentObjects, layoutAlgorithmRef, setInProgress, nodeIdsPinnedOutOfGroupRef, onGetGraphGroupsNodes }) => async (data, groupIds, pinOutOfGroup) => {
    var _a, _b;
    if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph) || !documentId || !(data === null || data === void 0 ? void 0 : data.length) || !(groupIds === null || groupIds === void 0 ? void 0 : groupIds.length))
        return;
    const { regularUngroupedNodesMap, groupedNodesMap: allGroupedNodesMap } = getNodesMaps(graphComponentRef);
    const { groupedNodesMap, newGroups } = addHiddenGroup({
        graphComponentRef,
        documentObjects,
        data,
        groupsData: (_b = sourceDataRef.current) === null || _b === void 0 ? void 0 : _b.DataGroups,
        groupIds,
        groupedNodesMap: pick(allGroupedNodesMap, groupIds)
    });
    const setInProgressCallback = setInProgress
        ? (inProgress) => {
            timer && clearTimeout(timer);
            if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected)
                return;
            if (inProgress) {
                setInProgress && setInProgress(true);
                return;
            }
            timer = setTimeout(() => {
                if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected)
                    return;
                setInProgress && setInProgress(false);
            }, UNDO_ENGINE_AUTO_MERGE_TIME * 1.5);
        }
        : undefined;
    setInProgressCallback === null || setInProgressCallback === void 0 ? void 0 : setInProgressCallback(true);
    await new Promise(processResolve => {
        setTimeout(() => {
            if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected) {
                processResolve(false);
                return;
            }
            const affectedNodes = new Set(values(newGroups));
            const nodesToRemove = new Set();
            const edgesToRemove = new Set();
            const dataNodeIds = map(data, "NodeId");
            const groupsLayout = {};
            forEach(groupedNodesMap, (groupGraphNode, groupId) => {
                var _a;
                if (!graphComponentRef.current || !((_a = groupGraphNode.tag) === null || _a === void 0 ? void 0 : _a.Nodes))
                    return;
                groupsLayout[groupId] = groupGraphNode.layout.toRect();
                const groupGraphTag = groupGraphNode.tag;
                const prevGroupNodes = groupGraphTag.Nodes;
                let newGroupNodes = [];
                let newNodeIdsPinnedOutOfGroup = [];
                if (pinOutOfGroup) {
                    newNodeIdsPinnedOutOfGroup = nodeIdsPinnedOutOfGroupRef.current[groupId] || [];
                    forEach(prevGroupNodes, p => {
                        if (includes(dataNodeIds, p.NodeId)) {
                            newNodeIdsPinnedOutOfGroup.push(p.NodeId);
                        }
                        else {
                            newGroupNodes.push(p);
                        }
                    });
                }
                else {
                    const pinToGroup = map(filter(data, ["GroupId", groupId]), e => nodeSourceFactory({ ...e, PinnedToGroup: true }, documentObjects));
                    const pinToGroupNodeIds = map(pinToGroup, "NodeId");
                    newGroupNodes = [...pinToGroup, ...prevGroupNodes];
                    newNodeIdsPinnedOutOfGroup = difference(nodeIdsPinnedOutOfGroupRef.current[groupId], pinToGroupNodeIds);
                }
                const groupObjectCountDiff = newGroupNodes.length - prevGroupNodes.length;
                const groupObjectCount = (groupGraphTag.GroupObjectCount || prevGroupNodes.length) + groupObjectCountDiff;
                if (groupObjectCount > 0) {
                    const updatedGroupTag = new GroupNodesSource(groupGraphTag.GroupId, newGroupNodes, groupGraphTag.DocumentObjects, groupGraphTag.NodeWidth || NODE_GROUP_MIN_SIZE.width, groupGraphTag.NodeHeight || NODE_GROUP_MIN_SIZE.height, groupGraphTag.GroupName, groupObjectCount);
                    groupGraphNode.tag = updatedGroupTag;
                    nodeIdsPinnedOutOfGroupRef.current[groupId] = newNodeIdsPinnedOutOfGroup;
                    addOrUpdateGroupNodesLabel(graphComponentRef, groupGraphNode);
                }
                else {
                    nodesToRemove.add(groupGraphNode);
                }
            });
            onGetGraphGroupsNodes(documentId, graphComponentRef, values(groupedNodesMap), nodeIdsPinnedOutOfGroupRef.current);
            forEach(data, d => {
                if (!d.GroupId || !graphComponentRef.current || !groupedNodesMap[d.GroupId])
                    return;
                const currentGraphGroup = groupedNodesMap[d.GroupId];
                const currentGraphGroupEdges = graphComponentRef.current.graph
                    .edgesAt(currentGraphGroup)
                    .toArray();
                if (pinOutOfGroup) {
                    const layout = d.GroupId ? groupsLayout[d.GroupId] : undefined;
                    const newNode = createNode(graphComponentRef, documentObjects ? documentObjects[d.DocumentObjectId] : undefined, { ...d, PinnedToGroup: false }, layout
                        ? new Rect(layout.centerX - NODE_MIN_SIZE.width / 2 + Math.floor(Math.random() * 100), layout.centerY - NODE_MIN_SIZE.height / 2 + Math.floor(Math.random() * 100), NODE_MIN_SIZE.width, NODE_MIN_SIZE.height)
                        : undefined);
                    if (newNode) {
                        if (graphComponentRef.current) {
                            graphComponentRef.current.selection.isSelected(currentGraphGroup) &&
                                graphComponentRef.current.selection.setSelected(newNode, true);
                        }
                        affectedNodes.add(newNode);
                        forEach(currentGraphGroupEdges, graphEdge => {
                            var _a;
                            if (!(graphEdge === null || graphEdge === void 0 ? void 0 : graphEdge.tag))
                                return;
                            const newUngroupedEdges = [];
                            if (!((_a = graphEdge.tag) === null || _a === void 0 ? void 0 : _a.Edges)) {
                                const graphEdgeTag = graphEdge.tag;
                                if (graphEdgeTag.From === d.NodeId || graphEdgeTag.To === d.NodeId) {
                                    newUngroupedEdges.push(graphEdgeTag);
                                    edgesToRemove.add(graphEdge);
                                }
                            }
                            else {
                                const graphEdgeTag = graphEdge.tag;
                                const newGroupedEdges = [];
                                forEach(graphEdgeTag.Edges, e => {
                                    if (e.From === d.NodeId || e.To === d.NodeId)
                                        newUngroupedEdges.push(e);
                                    else
                                        newGroupedEdges.push(e);
                                });
                                if (newGroupedEdges.length !== graphEdgeTag.Edges.length) {
                                    if (newGroupedEdges.length) {
                                        graphEdge.tag = {
                                            ...omit(graphEdge.tag, ["EdgeId", "Edges"]),
                                            Edges: newGroupedEdges
                                        };
                                    }
                                    else {
                                        edgesToRemove.add(graphEdge);
                                    }
                                }
                            }
                            if (newUngroupedEdges.length) {
                                createEdge(graphComponentRef, newUngroupedEdges, graphEdge.sourceNode === currentGraphGroup ? newNode : graphEdge.sourceNode, graphEdge.targetNode === currentGraphGroup ? newNode : graphEdge.targetNode);
                            }
                        });
                    }
                }
                else if (regularUngroupedNodesMap[d.GroupId] &&
                    regularUngroupedNodesMap[d.GroupId][d.NodeId] &&
                    graphComponentRef.current) {
                    const nodeToRemove = regularUngroupedNodesMap[d.GroupId][d.NodeId];
                    const edgesAtNodeToRemove = graphComponentRef.current.graph
                        .edgesAt(nodeToRemove)
                        .toArray();
                    forEach(edgesAtNodeToRemove, edgeAtNodeToRemove => {
                        var _a;
                        const siblingNode = edgeAtNodeToRemove.sourceNode !== nodeToRemove
                            ? edgeAtNodeToRemove.sourceNode
                            : edgeAtNodeToRemove.targetNode;
                        const graphGroupEdge = siblingNode
                            ? find(currentGraphGroupEdges, (e) => {
                                var _a;
                                return Boolean(((_a = e.tag) === null || _a === void 0 ? void 0 : _a.Edges) && (e.sourceNode === siblingNode || e.targetNode === siblingNode));
                            })
                            : undefined;
                        if (!((_a = graphGroupEdge === null || graphGroupEdge === void 0 ? void 0 : graphGroupEdge.tag) === null || _a === void 0 ? void 0 : _a.Edges)) {
                            createEdge(graphComponentRef, [edgeAtNodeToRemove.tag], edgeAtNodeToRemove.sourceNode === nodeToRemove
                                ? currentGraphGroup
                                : edgeAtNodeToRemove.sourceNode, edgeAtNodeToRemove.targetNode === nodeToRemove
                                ? currentGraphGroup
                                : edgeAtNodeToRemove.targetNode);
                        }
                        else {
                            const newEdges = [edgeAtNodeToRemove.tag, ...graphGroupEdge.tag.Edges];
                            graphGroupEdge.tag = {
                                ...graphGroupEdge.tag,
                                Edges: newEdges
                            };
                        }
                    });
                    nodesToRemove.add(nodeToRemove);
                }
            });
            forEach(Array.from(nodesToRemove), item => {
                var _a, _b;
                if ((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph.contains(item)) {
                    (_b = graphComponentRef.current) === null || _b === void 0 ? void 0 : _b.graph.remove(item);
                }
            });
            forEach(Array.from(edgesToRemove), item => {
                var _a, _b;
                if ((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph.contains(item)) {
                    (_b = graphComponentRef.current) === null || _b === void 0 ? void 0 : _b.graph.remove(item);
                }
            });
            const edges = new Set(graphComponentRef.current.graph.edges);
            morphLayout(graphComponentRef, layoutAlgorithmRef, affectedNodes, edges, setInProgressCallback, () => {
                processResolve(true);
            }, () => {
                processResolve(false);
            });
        });
    });
};
